import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import CheckTinyIcon from '../../icons/react/icon-check-tiny';
import XIcon from '../../icons/react/icon-x';
import { HorizontalStack } from '../alignment/horizontal-stack';
import { VerticalStack } from '../alignment/vertical-stack';
import type { ReadonlyListItem } from '../list/readonly/item/readonly-list-item';
import { ReadonlyList } from '../list/readonly/readonly-list';
import { Skeleton } from '../loading/skeleton';
import { Typography } from '../typography';
import styles from './permission.module.scss';

export interface PermissionProps {
  hideSeparator?: boolean;
  isPending: boolean;
  items?: ReadonlyListItem[];
  name: ReactNode;
  notInEffect?: boolean;
  suffix?: ReactNode;
  value?: boolean;
}

export function Permission({ hideSeparator, isPending, items, name, notInEffect, suffix, value }: PermissionProps) {
  const Icon = value ? CheckTinyIcon : XIcon;

  return (
    <VerticalStack
      className={clsx(styles.permission, isPending && styles.isPending, hideSeparator && styles.hideSeparator, notInEffect && styles.notInEffect)}
    >
      <HorizontalStack blockAlign="center" className={styles.booleanContainer} gap={2} wrap="nowrap">
        <VerticalStack align="center" className={clsx(styles.icon, value ? styles.isActive : undefined)} inlineAlign="center">
          {isPending ? <Skeleton fillParent parentClassName={styles.iconLoading} /> : <Icon aria-hidden height={16} />}
        </VerticalStack>

        {isPending ? (
          <Skeleton allowExpand className={styles.nameLoading} fillParent type="line" />
        ) : (
          <Typography lineHeight="1.125rem" size="0.875rem" tone={notInEffect ? 'neutral-dk' : 'md'}>
            {name}
          </Typography>
        )}

        {suffix && !isPending ? <div className={styles.suffix}>{suffix}</div> : null}
      </HorizontalStack>

      {items?.length && value ? (
        <ReadonlyList className={styles.listContainer} defaultRenderWhenEmpty="--" isPending={isPending} items={items} />
      ) : null}
    </VerticalStack>
  );
}
